import { fg } from '@atlassian/jira-feature-gating';
import {
	FormStatus,
	FormVisibility,
	type Form,
	type FormAnswers,
	type SelectOption,
	type TemplateForm,
	type UnsavedForm,
} from '@atlassian/proforma-common-core/form-system-models';
import { convertTemplateFormToUnsavedForm } from '@atlassian/proforma-common-core/form-system-utils';
import {
	type ApiUtil,
	AcceptedResponseType,
} from '@atlassian/proforma-common-core/jira-common-apis';
import type {
	AffectedServicesResponse,
	ApiFieldDataRequest,
	ApiFieldDataResponse,
	ApiFormChoicesResponse,
	AvailableForms,
	CmdbAutocompleteResponse,
	FoundAssignableUser,
	FoundUsers,
	IssueIndexForm,
	SuggestedJiraLabelsResponse,
} from '@atlassian/proforma-common-core/jira-common-models';
import type { IssueViewFormApi } from './issue-view-form-api.tsx';

export class LiveIssueViewFormApi implements IssueViewFormApi {
	public constructor(
		private util: ApiUtil,
		private projectId: number, // Deprecated, do not use.
		private issueId: number, // Deprecated, do not use.
		// eslint-disable-next-line no-empty-function
	) {}

	getFormIndex(issueId: number): Promise<IssueIndexForm[]> {
		return this.util.get(`/api/3/issue/${issueId}/formindex`);
	}

	getForm(formId: number): Promise<Form> {
		return this.util.get(`/api/3/issue/${this.issueId}/form/${formId}`);
	}

	public submitForm(issueId: number, formId: number, lock?: boolean): Promise<Form> {
		return this.util.put(`/api/3/issue/${issueId}/form/${formId}/${lock ? 'lock' : 'submit'}`, {});
	}

	public reopenForm(issueId: number, formId: number, formStatus?: FormStatus): Promise<Form> {
		if (formStatus === FormStatus.Locked) {
			return this.util.put(`/api/3/issue/${issueId}/form/${formId}/unlock`, {});
		}
		return this.util.put(`/api/3/issue/${issueId}/form/${formId}/reopen`, {});
	}

	getFormChoices(formId: number): Promise<ApiFormChoicesResponse> {
		return this.util.get(
			`/api/3/issue/${this.issueId}/form/${formId}/formchoices?allowSubmittedForm=true`,
		);
	}

	postFieldData(formId: number, request: ApiFieldDataRequest): Promise<ApiFieldDataResponse> {
		return this.util.post(`/api/cloud/issue/${this.issueId}/form/${formId}/fielddata`, request);
	}

	searchUsers(jiraField: string | undefined, query: string): Promise<SelectOption[]> {
		if (jiraField === 'assignee') {
			return this.util
				.jiraGet(
					`/rest/api/3/user/assignable/search?project=${this.projectId}&query=${encodeURIComponent(query)}`,
				)
				.then((data: FoundAssignableUser[]) =>
					data.map((user) => ({
						value: user.accountId,
						label: user.displayName ?? '',
					})),
				);
		}
		return this.util
			.jiraGet(`/rest/api/3/user/picker?query=${encodeURIComponent(query)}`)
			.then((data: FoundUsers) =>
				data.users.map((user) => ({
					value: user.accountId,
					label: user.displayName ?? '',
				})),
			);
	}

	searchUsersWithFieldConfig(
		jiraField: string | undefined,
		fieldConfigId: string | undefined,
		projectId: number,
		query: string,
	): Promise<SelectOption[]> {
		if (jiraField === 'assignee') {
			return this.util
				.jiraGet(
					`/rest/api/3/user/assignable/search?project=${this.projectId}&query=${encodeURIComponent(query)}`,
				)
				.then((data: FoundAssignableUser[]) =>
					data.map((user) => ({
						value: user.accountId,
						label: user.displayName ?? '',
					})),
				);
		}
		if (jiraField && fieldConfigId) {
			return this.util
				.jiraGet(
					`/rest/api/1.0/users/picker?fieldName=${jiraField}&fieldConfigId=${fieldConfigId}&projectId=${projectId}&showAvatar=true&query=${encodeURIComponent(
						query,
					)}`,
				)
				.then((data: FoundUsers) =>
					data.users.map((user) => ({
						value: user.accountId,
						label: user.displayName ?? '',
					})),
				);
		}
		return this.util
			.jiraGet(`/rest/api/3/user/picker?query=${encodeURIComponent(query)}`)
			.then((data: FoundUsers) =>
				data.users.map((user) => ({
					value: user.accountId,
					label: user.displayName ?? '',
				})),
			);
	}

	searchLabels(issueId: number, jiraField: string, query: string): Promise<SelectOption[]> {
		let uri = '';
		const isCustomLabelsField = jiraField.startsWith('customfield_');
		if (isCustomLabelsField) {
			const customFieldId = jiraField?.split('_')[1];
			uri = `/rest/api/1.0/labels/${issueId}/suggest?customFieldId=${customFieldId}&query=${query || ''}`;
		} else {
			uri = `/rest/api/1.0/labels/${issueId}/suggest?query=${query || ''}`;
		}
		return (
			this.util
				.jiraGet(uri)
				.then<SelectOption[]>((data: SuggestedJiraLabelsResponse) =>
					data.suggestions.map((jiraLabel) => ({
						value: jiraLabel.label,
						label: jiraLabel.label,
					})),
				)
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				.catch((e: any) => {
					// eslint-disable-next-line no-console
					console.warn(e);
					throw e;
				})
		);
	}

	searchAffectedServices(query?: string): Promise<SelectOption[]> {
		const uri = `/rest/service-registry-api/service?query=${query ?? ''}`;
		return (
			this.util
				.jiraGet(uri)
				.then<SelectOption[]>((data: AffectedServicesResponse) =>
					data.services.map((affectedService) => ({
						value: affectedService.id,
						label: affectedService.name,
					})),
				)
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				.catch((e: any) => {
					// eslint-disable-next-line no-console
					console.warn(e);
					throw e;
				})
		);
	}

	searchCmdb(
		jiraField: string,
		query: string | undefined,
		issueId: number,
		issueFormId: number,
		fieldConfigId: string,
		fieldValueMap: Record<string, string>,
	): Promise<SelectOption[]> {
		const uri = `/rest/servicedesk/cmdb/1/proforma/field/${jiraField}/config/${fieldConfigId}/autocomplete`;
		return this.util
			.jiraPost(
				uri,
				{
					query,
					issueId,
					issueFormId,
					fieldValueMap,
				},
				'cmdbAutocomplete',
			)
			.then<SelectOption[]>((data: CmdbAutocompleteResponse) =>
				data.results.map((cmdbObject) => ({
					value: cmdbObject.id,
					label: cmdbObject.label,
					...(fg('proforma_asset_fields_show_selected_attributes') ? { cmdbObject } : {}),
				})),
			);
	}

	public findAvailableFormsForIssue(issueId: number): Promise<AvailableForms> {
		return this.util.get(`/api/1/issues/${issueId}/availableforms`);
	}

	public getFormPreview(issueId: number, templateFormId: number): Promise<UnsavedForm> {
		return this.util
			.get(`/api/2/issues/${issueId}/availableforms/${templateFormId}/preview`)
			.then(
				(templateForm: TemplateForm): UnsavedForm => convertTemplateFormToUnsavedForm(templateForm),
			);
	}

	public addForm(issueId: number, templateFormId: number): Promise<Form> {
		return this.util.post(`/api/2/issues/${issueId}/forms`, {
			templateFormId,
		});
	}

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	public deleteForm(issueId: number, formId: number): Promise<any> {
		return this.util.delete(`/api/1/issues/${issueId}/forms/${formId}`);
	}

	public updateFormVisibility(issueId: number, formId: number, isInternal: boolean): Promise<void> {
		return this.util.post(`/api/2/issues/${issueId}/forms/${formId}`, {
			visibility: isInternal ? FormVisibility.Internal : FormVisibility.External,
		});
	}

	public saveAnswers(issueId: number, formId: number, answers: FormAnswers): Promise<Form> {
		return this.util.put(`/api/2/issues/${issueId}/forms/${formId}`, answers);
	}

	// User properties - for onboarding
	public getUserProperty(propertyKey: string, accountId: string): Promise<void> {
		return this.util.jiraGet(
			`/rest/api/latest/user/properties/${propertyKey}?accountId=${accountId}`,
			'userPropertyOnboarding',
		);
	}

	public setUserProperty(propertyKey: string, accountId: string, hasSeen: boolean): Promise<void> {
		return this.util.jiraPut(
			`/rest/api/latest/user/properties/${propertyKey}?accountId=${accountId}`,
			hasSeen,
			'userPropertyOnboarding',
		);
	}

	public getPdfDownloadUrl(issueid: number, formId: number): string {
		return `/api/1/issues/${issueid}/forms/${formId}/pdf`;
	}

	public getXlsxDownloadUrl(issueid: number, formId: number): string {
		return `/api/1/issues/${issueid}/forms/${formId}/spreadsheet`;
	}

	public get<T>(
		path: string,
		requestName?: string,
		responseType: AcceptedResponseType = AcceptedResponseType.Json,
	): Promise<T> {
		return this.util.get(path, requestName, responseType);
	}
}
